"use client"

import * as React from "react"
import { Label } from "@/components/ui/label"
import { useQueryClient } from '@tanstack/react-query'
import { TrendingUp, CheckCircle2, XCircle, Loader2 } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { facebookService, FacebookServiceError } from "@/services/facebookService"
import {
  facebookOAuthService,
  FacebookOAuthError,
  FACEBOOK_ADS_LEADS_LOGIN_SCOPES,
} from "@/services/facebookOAuthService"
import { queryKeys, useFacebookStatusQuery } from '@/queries'

interface FacebookAdsLeadsConnectionProps {
  translations?: Record<string, unknown>
}

export function FacebookAdsLeadsConnection({ translations }: FacebookAdsLeadsConnectionProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { data: connectionStatus, error: statusQueryError, isLoading: initialLoading } = useFacebookStatusQuery()

  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const getTranslation = React.useCallback((key: string, defaultValue?: string): string => {
    if (translations) {
      const keys = key.split('.')
      let value: Record<string, unknown> = translations
      for (const k of keys) {
        value = value?.[k] as Record<string, unknown>
      }
      return typeof value === 'string' ? value : (defaultValue || key)
    }
    return defaultValue ? t(key, defaultValue) : t(key)
  }, [translations, t])

  const statusErrorMessage = React.useMemo(() => {
    if (!statusQueryError) {
      return null
    }

    return statusQueryError instanceof FacebookServiceError
      ? statusQueryError.message
      : getTranslation('settings.facebook_ads.error_loading')
  }, [getTranslation, statusQueryError])

  const handleConnect = async () => {
    try {
      setLoading(true)
      setError(null)

      const accessToken = await facebookOAuthService.login(
        undefined,
        'token',
        FACEBOOK_ADS_LEADS_LOGIN_SCOPES
      )

      if (!accessToken) {
        throw new Error('No access token received')
      }

      try {
        const pages = await facebookService.getFacebookPages(accessToken)
        const singlePage = pages.length === 1 ? pages[0] : null

        const status = singlePage
          ? await facebookService.connectFacebookWithPage(
              accessToken,
              singlePage.id,
              singlePage.access_token,
              singlePage.name
            )
          : await facebookService.connectFacebook(accessToken)

        queryClient.setQueryData(queryKeys.meta.facebookStatus(), status)
      } catch (connectError) {
        console.error('Error completing Facebook Ads & Leads connection:', connectError)

        const status = await facebookService.connectFacebook(accessToken)
        queryClient.setQueryData(queryKeys.meta.facebookStatus(), status)
      }
    } catch (err) {
      console.error('Error connecting to Facebook Ads & Leads:', err)
      if (err instanceof FacebookOAuthError) {
        if (err.message.includes('cancelled') || err.message.includes('did not authorize')) {
          setError(null)
        } else {
          setError(err.message)
        }
      } else {
        setError(
          err instanceof FacebookServiceError
            ? err.message
            : getTranslation('settings.facebook_ads.error_connecting') || 'Failed to connect to Facebook Ads & Leads'
        )
      }
    } finally {
      setLoading(false)
    }
  }

  const handleDisconnect = async () => {
    try {
      setLoading(true)
      setError(null)

      await facebookService.disconnectFacebook()
      queryClient.setQueryData(queryKeys.meta.facebookStatus(), { connected: false })
    } catch (err) {
      console.error('Error disconnecting from Facebook Ads & Leads:', err)
      setError(err instanceof FacebookServiceError ? err.message : getTranslation('settings.facebook_ads.error_disconnecting'))
    } finally {
      setLoading(false)
    }
  }

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">{getTranslation('settings.facebook_ads.loading')}</span>
      </div>
    )
  }

  const resolvedConnectionStatus = connectionStatus ?? { connected: false }
  const isConnected = resolvedConnectionStatus.connected === true
  const displayedError = error ?? statusErrorMessage

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2 flex-shrink-0 relative z-10">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${isConnected
            ? 'bg-success/10 text-success'
            : 'bg-muted text-muted-foreground'
            }`}>
            {isConnected ? (
              <>
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap">{getTranslation('settings.facebook_ads.connected_status') || 'Connected'}</span>
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4 shrink-0" />
                <span className="text-sm font-medium whitespace-nowrap">{getTranslation('settings.facebook_ads.disconnected_status') || 'Not Connected'}</span>
              </>
            )}
          </div>
        </div>

        {isConnected ? (
          <Button
            variant="outline"
            size="default"
            onClick={handleDisconnect}
            disabled={loading}
            className="gap-2 shrink-0 w-full sm:w-auto relative z-0"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{getTranslation('settings.facebook_ads.loading')}</span>
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4" />
                <span>{getTranslation('settings.facebook_ads.disconnect_button') || 'Disconnect'}</span>
              </>
            )}
          </Button>
        ) : (
          <Button
            variant="default"
            size="default"
            onClick={handleConnect}
            disabled={loading}
            className="gap-2 shrink-0 w-full sm:w-auto relative z-0"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{getTranslation('settings.facebook_ads.loading')}</span>
              </>
            ) : (
              <>
                <TrendingUp className="h-4 w-4" />
                <span>{getTranslation('settings.facebook_ads.connect_button') || 'Connect Facebook Ads & Leads'}</span>
              </>
            )}
          </Button>
        )}
      </div>

      {displayedError && (
        <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20">
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="h-4 w-4" />
            <span>{displayedError}</span>
          </div>
        </div>
      )}

      {isConnected && connectionStatus && (
        <>
          <Separator />
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">{getTranslation('settings.facebook_ads.connection_info') || 'Connection Information'}</h3>

            {connectionStatus.user && (
              <div>
                <Label className="text-xs text-muted-foreground">
                  {getTranslation('settings.facebook_ads.user') || 'Facebook User'}
                </Label>
                <p className="text-sm mt-1">{connectionStatus.user.name}</p>
              </div>
            )}

            {connectionStatus.selected_page && (
              <div>
                <Label className="text-xs text-muted-foreground">
                  {getTranslation('settings.facebook_ads.page') || 'Selected Page'}
                </Label>
                <p className="text-sm mt-1">{connectionStatus.selected_page.name}</p>
              </div>
            )}
          </div>
        </>
      )}

      {!isConnected && !loading && (
        <div className="p-4 rounded-md bg-muted/50 border border-dashed">
          <p className="text-sm text-muted-foreground text-center">
            {getTranslation('settings.facebook_ads.description') || 'Connect your Facebook account to access ads campaigns and leads data.'}
          </p>
        </div>
      )}
    </div>
  )
}
